// src/pages/ForgetPassword.js or src/components/ForgetPassword.js
import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { GlobalContext } from '../context/GlobalContext';
import axios from 'axios';
import IconsSubmit from '../components/styled/IconsSubmit'; // if you have a spinner

const ForgetPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null); // { type: 'success'|'error', text: '...' }

  const { API_URL, showToast } = useContext(GlobalContext);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim() || !emailRegex.test(email)) {
      showToast('error', 'Please enter a valid email address', 'top', 8);
      return;
    }

    setIsLoading(true);
    setStatusMessage(null);
    showToast('pending', 'Sending reset link...', 'top');

    try {
      const response = await axios.post(`${API_URL}/api/forgetPassword`, { email });

      if (response.data.success) {
        setStatusMessage({ type: 'success', text: response.data.success });
        showToast('success', 'Reset link sent! Check your email', 'top', 10);
      } else if (response.data.failure) {
        setStatusMessage({ type: 'error', text: response.data.failure });
        showToast('error', response.data.failure, 'top', 10);
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to send reset link';
      setStatusMessage({ type: 'error', text: msg });
      showToast('failure', msg, 'top', 10);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Premium Card with 4-side Shadow */}
        <div className="bg-white rounded-2xl p-8 border border-gray-200 
                        shadow-[0_0_20px_rgba(0,0,0,0.24)] ring-1 ring-gray-900/5">

          <h1 className="text-3xl font-bold text-center text-gray-900 mb-2">
            Forgot Password?
          </h1>
          <p className="text-center text-gray-600 mb-8">
            Enter your email and we'll send you a reset link
          </p>

          {/* Success / Error Alert */}
          {statusMessage && (
            <div className={`mt-4 p-4 rounded-xl text-center font-medium transition-all
              ${statusMessage.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'}`}
            >
              {statusMessage.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6 mt-6">
            {/* Email Field with Floating Label */}
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="peer w-full px-4 py-4 text-gray-900 bg-transparent border-2 border-gray-300 rounded-xl 
                         focus:border-blue-600 focus:outline-none transition-all duration-300 placeholder-transparent"
                placeholder="Email"
              />
              <label
                className={`absolute left-4 -top-2.5 px-2 bg-white text-sm font-medium text-gray-600 
                  transition-all duration-300 
                  peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
                  peer-focus:-top-2.5 peer-focus:text-blue-600 peer-focus:text-sm
                  ${email ? '-top-2.5 text-blue-600 text-sm' : ''}`}
              >
                Email Address
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-4 rounded-xl font-semibold text-lg text-white transition-all duration-300 
                shadow-lg transform
                ${isLoading
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:shadow-2xl hover:-translate-y-0.5 active:translate-y-0'}`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-3">
                  <IconsSubmit />
                  Sending...
                </span>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>

          {/* Back to Login */}
          <p className="mt-8 text-center text-gray-600">
            Remember your password?{' '}
            <Link
              to="/login"
              className="font-semibold text-blue-600 hover:text-blue-800 hover:underline"
            >
              Back to Login
            </Link>
          </p>
        </div>

        <p className="text-center text-gray-500 text-sm mt-8">
          We’ll never share your email with anyone
        </p>
      </div>
    </div>
  );
};

export default ForgetPassword;